import { Box, SxProps, Theme } from '@mui/material';
import React, { useRef } from 'react';

import HorizontalScrollIndicator from '../horizontal-scroll-indicator/HorizontalScrollIndicator';

export interface ScrollableTableWrapperProps {
    /**
     * Table component to wrap
     */
    children: React.ReactNode;
    /**
     * MUI sx prop for styling the container
     */
    containerSx?: SxProps<Theme>;
    /**
     * Scroll amount in pixels for each arrow click
     * @default 200
     */
    scrollAmount?: number;
    /**
     * Show/hide scroll indicators
     * @default true
     */
    showIndicators?: boolean;
}

/**
 * ScrollableTableWrapper Component
 *
 * Wraps MUI Table components with horizontal scroll indicators for mobile/tablet views.
 * Automatically detects scroll position and shows/hides arrow buttons.
 *
 * @example
 * ```tsx
 * <ScrollableTableWrapper>
 *   <CommonTable
 *     columns={[...]}
 *     rows={[...]}
 *     sx={paymentTableSx}
 *   />
 * </ScrollableTableWrapper>
 * ```
 */
const ScrollableTableWrapper: React.FC<ScrollableTableWrapperProps> = ({
    children,
    scrollAmount = 200,
    showIndicators = true,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
            }}
        >
            {showIndicators && (
                <HorizontalScrollIndicator
                    containerRef={scrollContainerRef}
                    scrollAmount={scrollAmount}
                    show={showIndicators}
                />
            )}

            <Box ref={scrollContainerRef} sx={{ width: '100%', overflowX: 'auto' }}>
                {children}
            </Box>
        </Box>
    );
};

export default ScrollableTableWrapper;
